<template>
  <slot
    :value="effectValue"
    :unit="calculated?.unit"
    :is-special-effect="isSpecialEffect"
    :is-valid="isValid"
    :expression="expressionWithOperator"
    :expression-context="expressionContext"
    :affected-item="affectedItem"
  >
    <span>{{ effectValue }}</span>
    <expression-rendered
      :expression="expressionWithOperator"
      :parse-context="expressionContext"
      class="effect-amount__expression -inline"
    ></expression-rendered>
  </slot>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { calculateExpression } from "../expression/expressionFunctions";
import { formatNumber, type NumberFormatOption } from "@/helpers/utility/Formatting";

import ExpressionRendered from "../expression/ExpressionRendered.vue";

import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type { ExpressionParseContext } from "./Expression";
import { useSettingsStore } from "@editor/store/settings.store";

const props = withDefaults(
  defineProps<{
    expression: string;
    effectId?: number | string;
    parseContext?: ExpressionParseContext;
    
    sourceType?: string;
    sourceId?: number | string;

    /**
     * Round: required for impulses
     */
    rounds?: Partial<SimulationRoundItem>[];
    roundIndex?: number;
    
    signRequired?: boolean;
    numberFormat?: NumberFormatOption;

    impulseType?: "effect" | "cost";
  }>(),
  {
    parseContext: () => ({}),
    signRequired: false,
    numberFormat: "pointsFlexible",
    impulseType: "effect"
  }
);

const { t } = useI18n();
const settings = useSettingsStore();

const expressionWithOperator = computed(() => {
  if (!props.signRequired) return props.expression;
  if (
    !props.expression ||
    props.expression.length == 0 ||
    ["-", "+", "="].includes(props.expression[0])
  ) {
    return props.expression;
  }
  return "+" + props.expression;
});

const isSpecialEffect = computed(() => {
  return /[^\d*/+-=.]/g.test(expressionWithOperator.value);
});

const contextRound = computed(() => {
  if (!props.rounds) return null;
  const roundIndex = props.roundIndex ?? props.rounds.length - 1;
  return props.rounds.find((r) => r.index == roundIndex) ?? null;
});

const effectIsRandom = computed(() => {
  return props.expression?.includes?.("rand") ?? false;
});

const effectImpulse = computed(() => {
  const context = props.parseContext ?? {};
  if (context.affectedId == null || context.affectedType == null) return null;
  if (context.affectedType != "target" && context.affectedType != "resource") return null;

  const affectedItemImpulseSourceValues =
    contextRound.value?.impulses?.[context.affectedType]?.[context.affectedId]
      ?.sourceValues;
  
  return (
    affectedItemImpulseSourceValues?.find?.(
      (source) => {
        if (props.sourceType != null && props.sourceId != null) {
          if (source.type != props.sourceType) return false;
          if (source.id != props.sourceId) return false;
        }
        return source.effectId === props.effectId && source.effectType == props.impulseType
      }
    ) ?? null
  );
});

const expressionContext = computed(() => {
  const ctx: ExpressionParseContext & { roundIndex: number } = {
    roundIndex: props.roundIndex ?? 0,

    simulationSettings: {
      roundUnit: settings.simulationRoundUnit,
      roundLength: settings.simulationRoundLength,
      roundAmount: settings.simulationRoundAmount,
      durationType: settings.simulationDurationType,
      timeStart: settings.simulationTimeStart
    },
    
    ...(props.parseContext || {}),

    fixedRandom: effectImpulse.value?.randomValue
  };

  if (props.rounds) {
    ctx.rounds = props.rounds;
    ctx.roundIndex = contextRound.value?.index || 0;
  }

  return ctx;
});

const effectValue = computed(() => {
  const expressionString = expressionWithOperator.value;

  if (expressionString.startsWith("=")) {
    let evaluatedString = parseAndFormatExpression(
      expressionString.substring(1),
      expressionContext.value
    );
    if (evaluatedString.startsWith("+")) {
      evaluatedString = evaluatedString.substring(1);
    }
    return "=" + evaluatedString;
  }

  if (effectImpulse.value != null) {
    return formatValue(effectImpulse.value.value, expressionString);
  }

  return parseAndFormatExpression(expressionString, expressionContext.value);
});

const calculated = computed(() => {
  try {
    return calculateExpression(
      expressionWithOperator.value,
      expressionContext.value
    );
  } catch (error) {
    console.warn(error);
    return null;
  }
});

const isValid = computed(() => {
  if (!isSpecialEffect.value) return true;
  return calculated.value != null;
});

const affectedItem = computed(() => {
  const ctx = expressionContext.value;
  if (ctx.affectedId == null) return null;
  if (ctx.affectedType == "target") {
    return ctx.targets?.[ctx.affectedId] || null;
  }
  if (ctx.affectedType == "resource") {
    return ctx.resources?.[ctx.affectedId] || null;
  }
  return null;
});

function parseAndFormatExpression(
  expressionString: string,
  context: ExpressionParseContext = {}
) {
  try {
    const calculated = calculateExpression(expressionString, context);

    if (effectIsRandom.value) {
      return t("texts.fromTo", { min: formatValue(calculated.minValue, expressionString), max: formatValue(calculated.maxValue, expressionString) });
    }

    return formatValue(Number(calculated.value), expressionString);
  } catch (e) {
    if (e instanceof Error) {
      if (e.message.includes("recursive")) {
        return t("errors.recursiveExpression");
      } else {
        console.warn(e);
      }
    } else {
      console.warn(e);
    }
    return t("errors.invalidExpression");
  }
}

function formatValue(value: number | null, expressionString = "+") {
  const op = expressionString.substring(0, 1);
  const formattedNumber = formatNumber(value, props.numberFormat);
  if (op == "=") return "=" + formattedNumber;
  return formattedNumber;
}
</script>

<style lang="scss"></style>